/*
  Cheerful Buddha header search: suggestions as you type.

  Every form[data-cb-search] (snippets/cb-header-search.liquid: the panel under the header bar and the
  box in the phone menu) is a plain GET form to the search page. This turns its input into an ARIA
  combobox: from 2 characters, 200ms after the last key, it asks Shopify's predictive search for
  sections/cb-search-suggest.liquid (Section Rendering API, under the visitor's language root) and lists
  products, collections, articles and pages, then a "See all results" option.

  Keyboard: Down / Up move through the suggestions (focus stays in the box, aria-activedescendant
  follows), Enter opens the highlighted one or searches, Escape closes the list (a second Escape is
  left to the header, which closes the panel or menu). A polite status line announces the number of
  suggestions or the no-results message. All text comes from the header's settings (data-label-*).
  Synonyms ("lions mane", "hot choc") belong to the Search & Discovery app, not this file.
*/
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var WAIT_MS = 200;
  var SECTION_ID = 'cb-search-suggest';
  var LIMIT = 6;
  var TYPES = ['products', 'collections', 'articles', 'pages'];

  function fill(text, values) {
    return String(text || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function CbSearch(form) {
    this.form = form;
    this.input = form.querySelector('[data-cb-search-input]');
    this.results = form.querySelector('[data-cb-search-results]');
    this.list = form.querySelector('[data-cb-search-list]');
    this.empty = form.querySelector('[data-cb-search-empty]');
    this.status = form.querySelector('[data-cb-search-status]');
    this.url = form.getAttribute('data-suggest-url');
    if (!this.input || !this.results || !this.list || !this.url || !window.fetch || !window.DOMParser) return;

    this.cache = {};
    this.timer = 0;
    this.request = null;
    this.active = -1;
    this.shown = '';
    this.state = 'idle';

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    form.addEventListener('focusout', this.onFocusout.bind(this));
    form.addEventListener('submit', this.hide.bind(this));
    this.ready = true;
  }

  CbSearch.prototype.label = function (key) {
    return this.form.getAttribute('data-label-' + key) || '';
  };

  CbSearch.prototype.query = function () {
    return this.input.value.replace(/\s+/g, ' ').trim();
  };

  CbSearch.prototype.types = function () {
    var self = this;
    return TYPES.filter(function (type) {
      return type === 'products' || self.label(type) !== '';
    });
  };

  CbSearch.prototype.onInput = function () {
    var q = this.query();
    clearTimeout(this.timer);
    if (q.length < MIN_CHARS) {
      this.abort();
      this.reset();
      return;
    }
    if (this.cache[q]) {
      this.render(q, this.cache[q]);
      return;
    }
    this.timer = setTimeout(this.load.bind(this, q), WAIT_MS);
  };

  CbSearch.prototype.abort = function () {
    if (this.request) this.request.abort();
    this.request = null;
  };

  CbSearch.prototype.load = function (q) {
    var self = this;
    this.abort();
    var controller = window.AbortController ? new AbortController() : null;
    this.request = controller;
    var params = new URLSearchParams();
    params.set('q', q);
    params.set(
      'resources[type]',
      this.types()
        .map(function (type) {
          return type.slice(0, -1);
        })
        .join(',')
    );
    params.set('resources[limit]', String(LIMIT));
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    params.set('section_id', SECTION_ID);
    this.list.setAttribute('aria-busy', 'true');
    fetch(this.url + '?' + params.toString(), { signal: controller ? controller.signal : undefined })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        self.cache[q] = html;
        if (self.request === controller) self.request = null;
        if (self.query() === q) self.render(q, html);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        /* Suggestions are a help, not a gate: the form still searches on Enter. */
        if (self.query() === q) self.reset();
      })
      .then(function () {
        if (!self.request) self.list.removeAttribute('aria-busy');
      });
  };

  CbSearch.prototype.render = function (q, html) {
    var self = this;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var root = doc.querySelector('[data-cb-suggest]');
    var groups = root ? root.querySelectorAll('[data-cb-suggest-group]') : [];
    var base = this.list.id;
    var count = 0;
    var fragment = document.createDocumentFragment();

    Array.prototype.forEach.call(groups, function (group, index) {
      var type = group.getAttribute('data-cb-suggest-group');
      var heading = self.label(type);
      if (type !== 'products' && !heading) return;
      var head = group.querySelector('[data-cb-suggest-label="' + type + '"]');
      if (head && heading) {
        head.textContent = heading;
        head.id = base + '-g' + index;
        group.setAttribute('aria-labelledby', head.id);
      } else if (head) {
        head.remove();
      }
      ['from', 'sold_out'].forEach(function (key) {
        var text = self.label(key);
        group.querySelectorAll('[data-cb-suggest-label="' + key + '"]').forEach(function (el) {
          if (text) el.textContent = text;
          else el.remove();
        });
      });
      group.querySelectorAll('[role="option"]').forEach(function (option) {
        option.id = base + '-o' + count;
        count += 1;
      });
      fragment.appendChild(document.importNode(group, true));
    });

    if (count > 0 && this.label('see_all')) {
      var all = document.createElement('a');
      all.className = 'cb-header__sugg-all';
      all.setAttribute('role', 'option');
      all.setAttribute('aria-selected', 'false');
      all.tabIndex = -1;
      all.id = base + '-all';
      all.href = this.searchUrl(q);
      all.textContent = fill(this.label('see_all'), { terms: q });
      fragment.appendChild(all);
    }

    this.list.textContent = '';
    this.list.appendChild(fragment);
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.shown = q;
    this.state = count > 0 ? 'results' : 'none';

    var none = fill(this.label('none'), { terms: q });
    if (this.empty) {
      this.empty.textContent = count > 0 ? '' : none;
      this.empty.hidden = count > 0 || !none;
    }
    this.list.hidden = count === 0;
    this.say(count > 0 ? fill(this.label(count === 1 ? 'one' : 'other'), { count: count, terms: q }) : none);
    if (this.input === document.activeElement) this.show();
  };

  CbSearch.prototype.searchUrl = function (q) {
    var data = new FormData(this.form);
    data.set('q', q);
    var action = this.form.getAttribute('action') || '/search';
    return action + (action.indexOf('?') === -1 ? '?' : '&') + new URLSearchParams(data).toString();
  };

  CbSearch.prototype.say = function (text) {
    if (this.status) this.status.textContent = text || '';
  };

  CbSearch.prototype.options = function () {
    return this.list.querySelectorAll('[role="option"]');
  };

  CbSearch.prototype.isOpen = function () {
    return !this.results.hidden;
  };

  CbSearch.prototype.show = function () {
    if (this.state === 'idle' || (this.state === 'none' && this.empty && this.empty.hidden)) return;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', this.state === 'results' ? 'true' : 'false');
  };

  CbSearch.prototype.hide = function () {
    this.select(-1);
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  };

  CbSearch.prototype.reset = function () {
    this.hide();
    this.list.textContent = '';
    this.shown = '';
    this.state = 'idle';
    if (this.empty) this.empty.hidden = true;
    this.say('');
  };

  CbSearch.prototype.select = function (index) {
    var options = this.options();
    this.active = index;
    Array.prototype.forEach.call(options, function (option, i) {
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    var current = options[index];
    if (current) {
      this.input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  };

  CbSearch.prototype.onKeydown = function (event) {
    if (event.isComposing) return;
    var options = this.options();
    var open = this.isOpen() && this.state === 'results';
    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        if (!options.length || this.shown !== this.query()) return;
        event.preventDefault();
        if (!open) {
          this.show();
          this.select(0);
          return;
        }
        this.select(this.active + 1 >= options.length ? 0 : this.active + 1);
        return;
      case 'ArrowUp':
      case 'Up':
        if (!open) return;
        event.preventDefault();
        this.select(this.active <= 0 ? options.length - 1 : this.active - 1);
        return;
      case 'Enter':
        if (open && options[this.active]) {
          event.preventDefault();
          options[this.active].click();
        }
        return;
      case 'Escape':
      case 'Esc':
        if (this.isOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.hide();
        }
        return;
    }
  };

  CbSearch.prototype.onFocus = function () {
    if (this.shown && this.shown === this.query()) this.show();
  };

  CbSearch.prototype.onFocusout = function (event) {
    var next = event.relatedTarget;
    if (next instanceof Element && !this.form.contains(next)) this.hide();
  };

  function init() {
    document.querySelectorAll('form[data-cb-search]').forEach(function (form) {
      if (!form.cbSearch) form.cbSearch = new CbSearch(form);
    });
  }

  /* A tap or click outside the box closes its list (the panel itself is closed by the header). */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    document.querySelectorAll('form[data-cb-search]').forEach(function (form) {
      if (form.cbSearch && form.cbSearch.ready && !form.contains(target)) form.cbSearch.hide();
    });
  });

  /* Back and forward restore the page as it was left: drop suggestions for an old query. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    document.querySelectorAll('form[data-cb-search]').forEach(function (form) {
      if (form.cbSearch && form.cbSearch.ready) form.cbSearch.reset();
    });
  });

  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
